
import { useState } from 'react'
import { motion } from 'framer-motion'
import { Calendar, Clock, MapPin, Users, DollarSign, Share2, AlertTriangle, MoreVertical } from 'lucide-react'
import { Body } from '@meiuca/ui-components'
import { getNivelColors, getVagasColors, buildColorClasses, getParticipationStatusInfo } from '@/utils/gameUtils'
import { Game, getGameLevelLabel, getGameModalityLabel, ParticipantRole, ChargeType, formatGameTime, ParticipationStatus } from '@/types/game'
import { GameActions } from './GameActions'
import { ParticipantsList } from './ParticipantsList'

interface GameDetailsProps {
  game: Game
  isActionLoading?: string | null
  onShare?: (game: Game) => void
  onJoinGame?: (gameId: string) => void
  onConfirmParticipation?: (gameId: string) => void
  onDeclineInvitation?: (gameId: string) => void
  onCancelParticipation?: (gameId: string) => void
  onManageParticipant?: (gameId: string, participantId: string, action: 'approve' | 'reject' | 'remove') => void
  onCancelGame?: (gameId: string) => void
  onInviteUser?: (gameId: string, userId: string) => Promise<void>
  onInviteGuest?: (gameId: string, name: string, phoneNumber?: string) => Promise<void>
}

export function GameDetails({ 
  game,
  isActionLoading,
  onShare,
  onJoinGame,
  onConfirmParticipation,
  onDeclineInvitation,
  onCancelParticipation,
  onManageParticipant,
  onCancelGame,
  onInviteUser,
  onInviteGuest
}: GameDetailsProps) {
  const [showAdminMenu, setShowAdminMenu] = useState(false)

  const formatDateForCard = (date: Date) => {
    if (!date || isNaN(date.getTime())) return 'Invalid Date'
    return date.toLocaleDateString('pt-BR', {
      weekday: 'short',
      day: '2-digit',
      month: 'short'
    })
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price)
  }

  const getPaymentInfo = () => {
    if (game.paymentMode === ChargeType.DIVIDED) {
      const totalPrice = formatPrice(game.price)
      const pricePerPerson = game.totalSlots > 0 ? formatPrice(game.price / game.totalSlots) : formatPrice(0)
      return {
        mainPrice: totalPrice,
        description: `Total dividido entre ${game.totalSlots} pessoas`,
        perPerson: pricePerPerson,
        icon: '🧮',
        type: 'Dividido'
      }
    } else {
      return {
        mainPrice: formatPrice(game.price),
        description: 'Valor fixo por pessoa',
        perPerson: null,
        icon: '🎯',
        type: 'Fixo'
      }
    }
  }

  const vagasRestantes = game.totalSlots - game.occupiedSlots
  const nivelColors = getNivelColors(game.level)
  const vagasColors = getVagasColors(game.occupiedSlots, game.totalSlots)
  const isAdmin = game.userRole === ParticipantRole.ADMIN || game.userRole === ParticipantRole.OWNER
  const paymentInfo = getPaymentInfo()

  const statusInfo = getParticipationStatusInfo(game.userStatus)


  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-background-white rounded-xl shadow-sm p-4 sm:p-6 border border-border-primary"
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1 min-w-0 pr-4">
            <div className="mb-3">
              <h1 className="text-lg sm:text-xl font-semibold text-text-primary mb-2">{game.name}</h1>
              <div className="flex flex-wrap items-center gap-2">
                {statusInfo && (
                  <span className={`px-2 py-1 text-xs sm:text-sm font-medium rounded-lg border ${statusInfo.className}`}>
                    {statusInfo.label}
                  </span>
                )}
                <span className={`px-2 py-1 text-xs sm:text-sm font-medium rounded-lg ${buildColorClasses(vagasColors)}`}>
                  {vagasRestantes} {vagasRestantes === 1 ? 'vaga' : 'vagas'}
                </span>
                <span className={`px-2 py-1 text-xs rounded-full border ${buildColorClasses(nivelColors)}`}>
                  {getGameLevelLabel(game.level)}
                </span>
              </div>
            </div>
            <Body className="text-text-secondary">
              {getGameModalityLabel(game.modality)}
            </Body>
          </div>
          
          <div className="flex items-start space-x-2 flex-shrink-0">
            {onShare && (
              <button
                onClick={() => onShare(game)}
                className="p-2 hover:bg-background-secondary rounded-lg transition-colors"
                title="Compartilhar jogo"
              >
                <Share2 className="w-5 h-5 text-text-secondary" />
              </button>
            )}
          </div>
        </div>

        {/* Game Info Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Data e Hora */}
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <Calendar className="w-5 h-5 text-brand-secondary" />
              <div>
                <Body className="text-sm font-medium">Início</Body>
                <Body className="text-sm text-text-secondary">
                  {formatDateForCard(game.startDate)} às {formatGameTime(game.startDate)}
                </Body>
              </div>
            </div>
            {game.endDate && (
              <div className="flex items-center space-x-2">
                <Clock className="w-5 h-5 text-brand-secondary" />
                <div>
                  <Body className="text-sm font-medium">Término</Body>
                  <Body className="text-sm text-text-secondary">
                    {formatDateForCard(game.endDate)} às {formatGameTime(game.endDate)}
                  </Body>
                </div>
              </div>
            )}
          </div>

          {/* Local */}
          <div className="flex items-start space-x-2">
            <MapPin className="w-5 h-5 text-brand-secondary mt-0.5 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <Body className="text-sm font-medium truncate">{game.location}</Body>
              <Body className="text-sm text-text-secondary break-words">{game.address}</Body>
            </div>
          </div>

          {/* Participantes e Valor */}
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <Users className="w-5 h-5 text-brand-secondary" />
              <div>
                <Body className="text-sm font-medium">Participantes</Body>
                <Body className="text-sm text-text-secondary">
                  {game.occupiedSlots}/{game.totalSlots} jogadores
                </Body>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <DollarSign className="w-5 h-5 text-brand-secondary" />
              <div>
                <Body className="text-sm font-medium">
                  {paymentInfo.icon} {paymentInfo.type}
                </Body>
                <Body className="text-sm text-text-secondary">
                  <span className="font-medium">{paymentInfo.mainPrice}</span>
                  {paymentInfo.perPerson && (
                    <span className="text-xs ml-1">({paymentInfo.perPerson}/pessoa)</span>
                  )}
                </Body>
                <Body className="text-xs text-text-tertiary mt-0.5">
                  {paymentInfo.description}
                </Body>
              </div>
            </div>
          </div>
        </div>


        {/* Actions */}
        <div className="mt-4 sm:mt-6 pt-4 border-t border-border-primary">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <GameActions 
                game={game}
                isActionLoading={isActionLoading}
                onJoinGame={onJoinGame}
                onConfirmParticipation={onConfirmParticipation}
                onDeclineInvitation={onDeclineInvitation}
                onCancelParticipation={onCancelParticipation}
              />
            </div>
            
            {/* Admin Menu */}
            {isAdmin && onCancelGame && (
              <div className="relative">
                <button
                  onClick={() => setShowAdminMenu(!showAdminMenu)}
                  className="p-2 hover:bg-background-secondary rounded-lg transition-colors"
                  title="Opções de administrador"
                >
                  <MoreVertical className="w-5 h-5 text-text-secondary" />
                </button>
                
                {showAdminMenu && (
                  <>
                    <div 
                      className="fixed inset-0 z-10" 
                      onClick={() => setShowAdminMenu(false)}
                    />
                    <div className="absolute right-0 top-full mt-1 w-48 bg-background-white rounded-lg shadow-lg border border-border-primary z-20">
                      <div className="py-1">
                        <button
                          onClick={() => {
                            onCancelGame(game.id)
                            setShowAdminMenu(false)
                          }}
                          className="flex items-center space-x-2 w-full px-3 py-2 text-sm text-status-error-text hover:bg-status-error-bg transition-colors"
                        >
                          <AlertTriangle className="w-4 h-4" />
                          <span>Cancelar Jogo</span>
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Participants List */}
      {game.participants && (
        <ParticipantsList 
          participants={game.participants}
          game={game}
          isAdmin={isAdmin}
          confirmedCount={game.occupiedSlots}
          isActionLoading={isActionLoading}
          onManageParticipant={onManageParticipant ? (participantId, action) => onManageParticipant(game.id, participantId, action) : undefined}
          onInviteUser={onInviteUser}
          onInviteGuest={onInviteGuest}
        />
      )}
    </div>
  )
}